<template>
  <div class="space-y-3">
    <ModifierGroupInput
      v-for="group in modifierGroups"
      :key="group.id"
      ref="groupInputRefs"
      :modifier-group="group"
      :model-value="
        selectedModifierOptionIds[group.id] ?? []
      "
      :has-error="
        modifierGroupIdsWithErrors.includes(group.id)
      "
      @update:model-value="
        (value) => onModifierUpdate(group, value)
      "
    />
  </div>
</template>

<script setup lang="ts">
import type { CurrentOrderOrderItemModifier } from "@/composables/useCurrentOrder"
import type { ProductModifierGroup } from "@/services/product-modifiers/listProductModifiers"
import { useField } from "vee-validate"
import type ModifierGroupInput from "@/components/ModifierGroupInput.vue"

const props = defineProps<{
  modifierGroups: ProductModifierGroup[]
  modifierGroupIdsWithErrors: string[]
  name: string
}>()

const {
  value: selectedModifiers,
  setValue: setSelectedModifiers,
} = useField<CurrentOrderOrderItemModifier[]>(props.name)

const groupInputRefs = ref<
  Array<InstanceType<typeof ModifierGroupInput> | null>
>([])

// Scroll errored groups into view
watch(
  () => props.modifierGroupIdsWithErrors,
  (erroredIds) => {
    const firstErroredGroupIndex =
      props.modifierGroups.findIndex((group) =>
        erroredIds.includes(group.id)
      )

    if (
      firstErroredGroupIndex === -1 ||
      groupInputRefs.value.length <
        firstErroredGroupIndex + 1
    )
      return

    const firstErroredGroupRef =
      groupInputRefs.value[firstErroredGroupIndex]

    firstErroredGroupRef?.scrollIntoView()
  }
)

const onModifierUpdate = (
  group: ProductModifierGroup,
  optionIds: string[]
) => {
  setSelectedModifiers([
    ...selectedModifiers.value.filter(
      (m) => m.groupId !== group.id
    ),
    ...group.options
      .filter((o) => optionIds.includes(o.id))
      .map((o) => ({
        id: o.id,
        label: o.label,
        priceOffset: o.priceOffset,
        groupId: group.id,
      })),
  ])
}

const selectedModifierOptionIds = computed(() =>
  selectedModifiers.value.reduce(
    (accumulator: Record<string, string[]>, modifier) => {
      const key = modifier.groupId
      const value = modifier.id
      return {
        ...accumulator,
        [key]: accumulator[key]
          ? [...accumulator[key], value]
          : [value],
      }
    },
    {}
  )
)
</script>
